/* src/modules/office/components/DeviationRequestModal.js */
import React, { useState, useEffect } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { ClockIcon, ExclamationIcon } from '../../../components/icons/Icons';
import { formatDateLocal, isWeekend } from '../utils/dateHelpers'; // Import isWeekend

export default function DeviationRequestModal({ appId, userProfile, date, currentStatus, checkDateStatus, onClose }) {
    
    // 1. Determine Target Status
    const getInitialTarget = () => {
        if (currentStatus.type === 'WFO') return 'WFH';
        if (currentStatus.type === 'WFH') return 'WFO';
        return 'WFH'; 
    };

    const [type, setType] = useState(getInitialTarget());
    const [reason, setReason] = useState('');
    const [compensatoryDate, setCompensatoryDate] = useState('');
    const [compError, setCompError] = useState(''); // New Error State
    const [isSaving, setIsSaving] = useState(false);

    // 2. Calculate Compensatory Window
    const minDateStr = formatDateLocal(date);
    const maxCompDate = new Date(date);
    maxCompDate.setDate(maxCompDate.getDate() + 14); 
    const maxDateStr = formatDateLocal(maxCompDate);

    useEffect(() => {
        setType(getInitialTarget());
    }, [currentStatus]);

    // 3. Compensatory Date Validator
    const handleCompensatoryChange = (e) => {
        const val = e.target.value;
        setCompensatoryDate(val);
        setCompError(''); // Reset error

        if (!val) return;

        const compDateObj = new Date(val);
        
        // A. Check Weekend
        if (isWeekend(compDateObj)) {
            setCompError("You cannot select a weekend for compensatory work.");
            return;
        }

        // B. Check Schedule Status
        // checkDateStatus() is passed from parent. It returns { type: 'WFO'|'WFH'|'Holiday'|... }
        const status = checkDateStatus(compDateObj);

        if (status.type === 'Holiday') {
            setCompError(`Cannot select a Holiday (${status.label}).`);
            return;
        }

        if (status.type === 'WFO') {
            setCompError("This date is already scheduled as an Office day.");
            return;
        }
        
        // Optional: Block if they pick the SAME day (which is logically impossible for WFH request, but good safety)
        if (val === formatDateLocal(date)) {
             setCompError("Compensatory date cannot be the same as the leave date.");
             return;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (type === 'WFH' && compensatoryDate && compError) {
            alert("Please fix the compensatory date error before submitting.");
            return;
        }

        setIsSaving(true);
        try {
            await addDoc(collection(db, `artifacts/${appId}/schedule_deviations`), {
                userId: userProfile.id,
                userName: userProfile.name,
                managerId: userProfile.managerId,
                date: formatDateLocal(date),
                type,
                reason,
                compensatoryDate: type === 'WFH' ? compensatoryDate : null,
                status: 'Pending', 
                createdAt: serverTimestamp()
            });
            alert("Request sent successfully.");
            onClose();
        } catch (e) {
            console.error(e);
            alert("Failed to send request.");
        } finally {
            setIsSaving(false);
        }
    };

    const isFixedChange = currentStatus.type !== 'Unassigned';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-gray-50 p-5 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-[#213368]">Request Schedule Change</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        For <span className="font-bold text-gray-700">{date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                    </p>
                </div>

                <div className="p-6">
                    <div className="mb-6 bg-blue-50 p-3 rounded-lg border border-blue-100 flex items-start gap-3">
                        <ExclamationIcon className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="text-gray-500 text-xs uppercase font-bold">Current Status</p>
                            <p className="font-bold text-blue-800">{currentStatus.type} <span className="font-normal text-gray-600">({currentStatus.source})</span></p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Change To</label>
                            {isFixedChange ? (
                                <div className="p-3 bg-gray-100 rounded border border-gray-200 text-gray-700 font-bold">
                                    {type === 'WFO' ? 'Work From Office (WFO)' : 'Work From Home (WFH)'}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        type="button" onClick={() => setType('WFO')}
                                        className={`p-3 text-sm font-bold rounded border transition ${type === 'WFO' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        Office (WFO)
                                    </button>
                                    <button 
                                        type="button" onClick={() => setType('WFH')}
                                        className={`p-3 text-sm font-bold rounded border transition ${type === 'WFH' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        Home (WFH)
                                    </button>
                                </div>
                            )}
                        </div>

                        {type === 'WFH' && (
                            <div className={`p-4 rounded-lg border ${compError ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-100'}`}>
                                <label className={`flex items-center text-xs font-bold uppercase mb-2 ${compError ? 'text-red-800' : 'text-orange-800'}`}>
                                    <ClockIcon className="h-4 w-4 mr-1.5" /> 
                                    Compensatory Office Day <span className="ml-1 font-normal normal-case opacity-75">(Optional)</span>
                                </label>
                                <input 
                                    type="date" 
                                    className={`w-full p-2 border rounded text-sm outline-none ${compError ? 'border-red-300 focus:ring-2 focus:ring-red-200' : 'border-orange-200 focus:ring-2 focus:ring-orange-200'}`}
                                    min={minDateStr}
                                    max={maxDateStr}
                                    value={compensatoryDate} 
                                    onChange={handleCompensatoryChange} 
                                />
                                {compError ? (
                                    <p className="text-xs text-red-600 mt-2 font-bold flex items-center">
                                        <ExclamationIcon className="h-3 w-3 mr-1" /> {compError}
                                    </p>
                                ) : (
                                    <p className="text-[10px] text-orange-700 mt-2 leading-snug">
                                        Select a non-office day within <strong>2 weeks</strong> to make up for this WFH.
                                    </p>
                                )}
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Reason for Change</label>
                            <textarea 
                                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" 
                                rows="3" 
                                placeholder="e.g. Doctor's appointment, Personal emergency..."
                                value={reason} 
                                onChange={e => setReason(e.target.value)} 
                                required 
                            />
                        </div>

                        <div className="flex justify-end space-x-3 pt-2">
                            <button type="button" onClick={onClose} className="px-5 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition">Cancel</button>
                            <button type="submit" disabled={isSaving || !!compError} className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded-lg hover:opacity-90 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed">
                                {isSaving ? 'Sending...' : 'Send Request'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}